/* ============================================================================
   CRESTSIDE — Shopify runtime.

   The lab build was a single page with an in-memory cart. This keeps every bit
   of the approved presentation (gate ceremony, star field, swipe gallery,
   lightbox) and replaces the fake commerce with Shopify's real AJAX Cart API,
   so Add to cart creates an actual cart line and Checkout reaches Shopify's
   hosted checkout.

   Deliberately avoided, per a bug that once blanked the whole published page:
   no mask+animation on one element, no infinite animation faster than ~2s.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.CRESTSIDE || {};
  var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(pointer: fine)').matches;
  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return [].slice.call((r || document).querySelectorAll(s)); };

  /* Tells the watchdog in crestside-chrome.liquid that this file actually ran.
     If it never gets set -- blocked script, CDN failure, extension -- the
     watchdog opens the store rather than leaving the gate covering it forever. */
  window.__CS_BOOTED__ = true;

  /* sessionStorage THROWS (not returns null) in sandboxed iframes and some
     managed/privacy browser modes. An uncaught throw at the top of this IIFE
     aborts every line after it: no add-to-cart, no gallery, no cart drawer, and
     on the homepage a gate that can never open. Both accesses go through these. */
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* non-fatal */ } }

  /* `'$' + (cents/100).toFixed(2)` printed "$1290.00" in the cart while the
     product page, the sticky bar and Shopify's own checkout all said
     "$1,290.00 AUD". Three different renderings of the same number on the way to
     paying for it, on the most expensive piece in the drop. */
  function money(cents, withCurrency) {
    return '$' + (cents / 100).toLocaleString('en-AU', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }) + (withCurrency ? ' AUD' : '');
  }

  /* ---------------------------------------------------------------- GATE */
  var gate = $('#gate');
  var body = document.body;

  if (gate) {
    var starfield = $('#starfield');
    /* A `!still` guard used to sit here, skipping the seeding entirely under
       prefers-reduced-motion to avoid building 240 nodes for someone who asked
       the machine to do less. Removed 3 Aug, for two reasons.

       It broke the spec. The bible asks reduced motion for "static faint
       stars", not for the crown to be deleted -- and because the gate is the
       PERSISTENT site-wide backdrop, skipping it gave those visitors a
       starless sky on the homepage, every product page, the cart, About, Why
       925 and all five policies. It also left the reduced-motion rule in
       crestside.css as dead code that could never run.

       And its premise turned out to be wrong. The measurement behind the
       .star.still work is that the cost is ANIMATING layers, not element
       count: the frozen-star control ran the full field at 60fps with every
       one of those 240 nodes present. A star seeded `still` carries no
       animation, so this costs essentially nothing. */
    if (starfield) {
      /* ANIM_SHARE = the fraction of the field that actively twinkles. 1 means
         every star does, which is how the founder built it and how it ships.

         DO NOT LOWER THIS. It is not a free performance lever, whatever the
         measurements might tempt you into. It was set to 0.20 on phones and
         0.35 above on 3 Aug as a frame-rate fix, and the founder rejected it
         on sight the moment he saw it: "i liked the previous one better, it's
         one of my favourites, i tweaked it a lot of times to make it perfect
         and accurate according to me." Reverted 4 Aug.

         The cost of 1 is real, measured and honest -- roughly 23fps against 60
         on a throttled phone, with the full working in .star.still over in
         crestside.css. Accepting that cost is HIS call and he has now made it
         explicitly. If a future session needs frames back from this field, the
         only correct move is to ask him first. */
      var ANIM_SHARE = 1;
      var seed = function (topMin, topRange, sizeMax) {
        var s = document.createElement('i');
        /* Under reduced motion EVERY star is `still`; the reduced-motion block
           in crestside.css then pins them at opacity .3 -- the "static faint
           stars" the spec asks for. */
        s.className = (still || Math.random() >= ANIM_SHARE) ? 'star still' : 'star';
        s.style.left = (2 + Math.random() * 96) + 'vw';
        s.style.top = (topMin + Math.random() * topRange) + 'vh';
        s.style.setProperty('--s', (2.5 + Math.random() * sizeMax).toFixed(1) + 'px');
        s.style.setProperty('--d', (2.4 + Math.random() * 3.4).toFixed(2) + 's');
        s.style.setProperty('--dl', (Math.random() * 2.6).toFixed(2) + 's');
        s.style.setProperty('--o', (0.45 + Math.random() * 0.55).toFixed(2));
        starfield.appendChild(s);
      };
      /* 216 + 24 = 240, UNCONDITIONAL, matching the founder's own tuned build
         at lab/crestside-gate.html:1221-1222 and the count the design bible
         locks in two places ("216-star crown", and the Continuum rule's "EXACT
         same, zero visual break").

         RESTORED 4 Aug. A viewport-conditional version lived here from 31 Jul
         (commit 3983ed9): `wide < 560 ? 96 : (wide < 900 ? 150 : 216)`, which
         meant a phone rendered 108 stars instead of 240 -- 45% of the field he
         tuned. It was introduced as an answer to his "the mobile version is
         not smooth" note and was never his decision. He is emphatic that this
         field is his: "i tweaked it a lot of times to make it perfect and
         accurate according to me."

         The frame cost is real, measured and his to accept -- see .star.still
         in crestside.css. DO NOT thin this again without asking him. If the
         phone needs frames back, ask first; the honest lever is his to pull.

         HE PULLED IT, 10 Aug: "the screen was like flicking... it feels like
         low fps... fix all this properly." The lever chosen is the scroll
         freeze below, NOT a thinning: all 240 stars exist and all of them
         twinkle whenever the page is at rest. They go .still only while the
         page is actually scrolling and wake 180ms after it stops. Measured
         10->33fps during scroll on the throttled bench; the at-rest field is
         byte-identical to his tuning. */
      for (var i = 0; i < 216; i++) seed(1, 13, 4.5);
      for (var j = 0; j < 24; j++) seed(6, 36, 5.5);

      /* The scroll freeze. Passive listener, one class toggle each way. The
         `still` guard matters twice over: under reduced motion every star is
         already permanently .still, and removing the class here would wake
         animations the visitor asked not to see. */
      if (!still) {
        /* Only the stars seeded ANIMATED. If ANIM_SHARE is ever tuned below
           1 again, the wake pass must not resurrect the deliberately-still
           share. */
        var starNodes = Array.prototype.slice.call(starfield.children).filter(function (n) {
          return n.className === 'star';
        });
        var starWakeT = null;
        window.addEventListener('scroll', function () {
          if (!starNodes.length) return;
          if (starWakeT === null) {
            for (var k = 0; k < starNodes.length; k++) starNodes[k].classList.add('still');
          } else {
            clearTimeout(starWakeT);
          }
          starWakeT = setTimeout(function () {
            for (var k = 0; k < starNodes.length; k++) starNodes[k].classList.remove('still');
            starWakeT = null;
          }, 180);
        }, { passive: true });
      }
    }

    var enterBtn = $('#enter');
    var stage = $('#logo-stage');
    var tilt = $('#tilt');
    var cursorEl = $('#cursor');
    var entered = false;

    /* The logo is announced as a button from first paint, so it must DO
       something from first paint -- previously the handler was only attached
       after entering, leaving a focusable control that did nothing. */
    if (stage) {
      var goHome = function () { if (document.body.classList.contains('entered')) window.location.href = '/'; };
      stage.addEventListener('click', goHome);
      stage.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goHome(); }
      });
    }

    /* While the ceremony is up, everything behind it must be unreachable. The
       gate is opaque and fixed at z-index 100, but the store below stays in the
       tab order -- so Tab moved focus onto the cart button and product links
       that are painted over, with no visible ring anywhere. The page read as
       frozen. Deliberately NOT the shared INERT_TARGETS list, which includes
       #gate itself. */
    /* .cs-skip included 5 Aug: it was the one focusable thing NOT inert
       during the ceremony, and activating it fired the :target no-JS escape
       while JS still held #MainContent inert, ENTER display:none and the
       scroll locked -- a hard dead end (reproduced with a keyboard before
       fixing). The modal INERT_TARGETS list already carries it; the gate
       list had never caught up. */
    var GATE_INERT = ['#MainContent', '#cs-nav', '#cs-foot', '.cs-logo-fixed', '.cs-skip'];
    function setGateInert(on) {
      GATE_INERT.forEach(function (sel) {
        $$(sel).forEach(function (el) {
          if (on) el.setAttribute('inert', ''); else el.removeAttribute('inert');
        });
      });
    }

    /* Shown once per visit. Returning from checkout should not replay it. */
    if (ssGet('cs_entered') === '1') {
      body.classList.add('entered');
      gate.classList.add('site-bg');
      gate.style.zIndex = '1';
      /* Same modal demotion as dock(): on a return visit the ceremony
         elements are display:none and the visible logo is .cs-logo-fixed,
         outside #gate, so hiding the backdrop from the tree is safe. */
      gate.removeAttribute('role');
      gate.removeAttribute('aria-modal');
      gate.setAttribute('aria-hidden', 'true');
      var site0 = $('#site, #MainContent');
      if (site0) site0.removeAttribute('aria-hidden');
      body.classList.add('no-gate');
      entered = true;
    }

    /* Guarded on enterBtn, which only exists on the index template. On product
       and cart pages #gate is nothing but the decorative backdrop -- inerting
       #MainContent there would disable the entire store. */
    if (!entered && enterBtn) setGateInert(true);

    if (fine && !still && !entered && cursorEl) {
      cursorEl.classList.add('on');
      gate.addEventListener('mousemove', function (e) {
        if (entered) return;
        var mx = e.clientX / window.innerWidth - 0.5;
        var my = e.clientY / window.innerHeight - 0.5;
        if (tilt) tilt.style.transform = 'rotateX(' + (-my * 7).toFixed(2) + 'deg) rotateY(' + (mx * 9).toFixed(2) + 'deg)';
        if (starfield) starfield.style.transform = 'translate(' + (-mx * 16).toFixed(1) + 'px,' + (-my * 11).toFixed(1) + 'px)';
        cursorEl.style.transform = 'translate(' + e.clientX + 'px,' + e.clientY + 'px) translate(-50%,-50%)';
      });
      if (enterBtn) {
        enterBtn.addEventListener('mouseenter', function () { cursorEl.classList.add('big'); });
        enterBtn.addEventListener('mouseleave', function () { cursorEl.classList.remove('big'); });
      }
    }

    function enterSite() {
      if (entered) return;
      entered = true;
      ssSet('cs_entered', '1');
      setGateInert(false);

      var site = $('#site, #MainContent');
      if (site) site.removeAttribute('aria-hidden');
      body.classList.add('entered');
      if (cursorEl) cursorEl.classList.remove('on');
      if (tilt) tilt.style.transform = '';
      if (starfield) starfield.style.transform = '';
      gate.classList.add('site-bg');
      gate.style.zIndex = '1';

      /* The ENTER control is hidden the instant `entered` lands, and hiding the
         focused element drops focus to <body>. A keyboard visitor who crossed
         the threshold was therefore dumped back at the top of the document
         with nothing announced and no idea where they were. Move focus
         deliberately to the pieces grid -- which is the thing entering was
         FOR. tabindex is applied here rather than in the template so the
         section never becomes a tab stop of its own during normal browsing. */
      var dest = $('#pieces') || $('#MainContent');
      if (dest) {
        dest.setAttribute('tabindex', '-1');
        dest.focus({ preventScroll: true });
      }

      if (!stage) return;
      var em = $('.logo-emerge', stage);
      var r = em.getBoundingClientRect();
      document.body.appendChild(stage);

      /* Moving a node in the DOM RESTARTS its CSS animations. `emerge` starts at
         opacity 0 behind a delay, so reparenting the stage to <body> replayed it
         from scratch: measured opacity dropped from 1 to 0 the instant this ran
         and did not recover for ~500ms. The logo simply vanished for the first
         half of its own flight, leaving an empty screen between the gate and the
         grid -- the "gap in the middle" of the entry.

         Freeze it in its finished state before the browser paints the new
         position. Cleared, not overridden, so nothing can replay it again. */
      em.style.animation = 'none';
      em.style.opacity = '1';

      stage.classList.add('moved');
      stage.style.position = 'fixed';
      stage.style.left = r.left + 'px';
      stage.style.top = r.top + 'px';
      stage.style.margin = '0';
      stage.style.zIndex = '300';
      em.style.width = r.width + 'px';
      stage.style.cursor = 'pointer';

      /* clientWidth, NOT innerWidth. innerWidth counts the scrollbar; the
         initial containing block that dock()'s `left: 50%` resolves against
         does not. On a 1440 desktop with a 15px scrollbar the flight aimed
         at centre 720 while dock() landed on 712.5, so the logo lurched
         7.5px left at the end of its own travel (founder video, 5 Aug).
         Mobile is unaffected either way: overlay scrollbars mean
         clientWidth === innerWidth there. */
      var vw = document.documentElement.clientWidth;
      var targetW = Math.min(vw * 0.34, 196);
      var sc = targetW / r.width;
      var dx = (vw - targetW) / 2 - r.left;
      var dy = 18 - r.top;

      function dock() {
        /* The returning-visit branch adds `no-gate`; a live entry never did, so
           the only thing hiding the ceremony afterwards was
           `#gate.site-bg .enter-btn { opacity: 0 }` -- which leaves a real,
           labelled, focusable button sitting invisibly on the homepage. Tabbing
           across the page landed on a stop with nothing at it. `no-gate` carries
           the display:none rule, so both paths now end in the same state.

           It has to happen HERE, not at the top of enterSite(): that rule also
           matches .logo-stage while the stage is still inside #gate, so setting
           it early would display:none the logo before its own
           getBoundingClientRect() is read -- zeroing the flight and reopening
           the "gap in the middle" of the entry. By dock() the stage has been
           reparented out of #gate and the ENTER word has finished fading.

           A dedicated class, NOT `no-gate`: no-gate also un-hides .cs-logo-fixed
           (that is how a return visit gets a docked logo without the flight), and
           on a live entry the flown stage is already sitting in exactly that
           position -- so reusing it would paint two logos on top of each other. */
        body.classList.add('gate-done');
        /* The ceremony is over and the stage already lives on <body>, so the
           backdrop stops declaring itself an open modal. Leaving
           role="dialog" aria-modal="true" on the permanent background told
           assistive tech the entire store behind it was inert for the rest
           of the session (final master audit, 10 Aug). Non-index templates
           already get this demotion server-side; the homepage's live-entry
           path never did. Runs again on every resize re-dock, harmlessly. */
        gate.removeAttribute('role');
        gate.removeAttribute('aria-modal');
        gate.setAttribute('aria-hidden', 'true');
        var w = Math.min(document.documentElement.clientWidth * 0.34, 196);
        stage.classList.remove('travel');
        stage.style.transition = 'none';
        stage.style.transform = 'none';
        stage.style.left = '50%';
        stage.style.marginLeft = (-w / 2) + 'px';
        stage.style.top = '18px';
        em.style.width = w + 'px';
      }
      /* Re-dock on resize -- but NEVER mid-flight. On a real phone the URL bar
         collapses during the 1.15s travel and fires resize; an unguarded dock()
         killed the transition and teleported the logo ~127px to its final spot
         (measured 5 Aug, the founder's "logo moves a bit" complaint). gate-done
         is added by the first dock(), so this guard waits the flight out. A
         width change mid-flight leaves the flight aiming at a slightly stale
         centre; the 1150ms dock() recomputes from fresh viewport values and
         lands it exactly, so nothing is lost by waiting. */
      window.addEventListener('resize', function () {
        if (body.classList.contains('entered') && body.classList.contains('gate-done')) dock();
      });

      if (still) { dock(); return; }
      void stage.offsetWidth;
      stage.classList.add('travel');
      stage.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(' + sc + ')';
      setTimeout(dock, 1150);
    }

    if (enterBtn) {
      enterBtn.addEventListener('click', enterSite);
      enterBtn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); enterSite(); }
      });
    }

    if (!still) {
      var tick = false;
      window.addEventListener('scroll', function () {
        if (tick || !starfield) return;
        tick = true;
        requestAnimationFrame(function () {
          starfield.style.transform = 'translateY(' + (-window.scrollY * 0.08).toFixed(1) + 'px)';
          tick = false;
        });
      }, { passive: true });
    }
  }

  /* ------------------------------------------------------- PDP GALLERY */
  var pdpMedia = $('#pdp-media');
  var pdpDots = $('#pdp-dots');
  var lightbox = $('#lightbox');
  var lightboxImg = $('#lightbox-img');

  if (pdpMedia && pdpDots) {
    var shots = $$('.pdp-shot', pdpMedia);
    if (shots.length > 1) {
      shots.forEach(function (_, idx) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'pdp-dot';
        d.setAttribute('aria-label', 'Go to image ' + (idx + 1) + ' of ' + shots.length);
        d.setAttribute('aria-current', idx === 0 ? 'true' : 'false');
        d.addEventListener('click', function () {
          /* `still` honoured here too. The dots predate the reduced-motion pass
             and were the one control still animating its scroll. */
          pdpMedia.scrollTo({ left: pdpMedia.clientWidth * idx, behavior: still ? 'auto' : 'smooth' });
        });
        pdpDots.appendChild(d);
      });

      /* Founder direction after checking Broken Planet / Cactus Jack / Kuurth on
         a phone: the track already swiped, but nothing told you it could. Dots
         report a position; arrows invite the move. Measured on Kuurth's live
         mobile PDP: Previous/Next controls AND slide dots, both present. */
      var prev = $('#pdp-prev');
      var next = $('#pdp-next');

      function page(dir) {
        var w = pdpMedia.clientWidth;
        if (!w) return;
        var at = Math.round(pdpMedia.scrollLeft / w);
        var to = Math.max(0, Math.min(shots.length - 1, at + dir));
        pdpMedia.scrollTo({ left: w * to, behavior: still ? 'auto' : 'smooth' });
      }
      if (prev) prev.addEventListener('click', function () { page(-1); });
      if (next) next.addEventListener('click', function () { page(1); });

      /* Arrow keys move the gallery when it has focus, so the carousel is not
         pointer-only -- the images are already tab stops (they open the zoom). */
      pdpMedia.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); page(1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); page(-1); }
      });

      var dotTick = false;
      function syncGallery() {
        var w = pdpMedia.clientWidth;
        if (!w) return;
        var active = Math.round(pdpMedia.scrollLeft / w);
        var dots = pdpDots.children;
        for (var n = 0; n < dots.length; n++) {
          dots[n].setAttribute('aria-current', n === active ? 'true' : 'false');
        }
        /* An arrow with nothing left to do stops soliciting taps rather than
           silently doing nothing when pressed. */
        if (prev) prev.disabled = active <= 0;
        if (next) next.disabled = active >= shots.length - 1;
      }
      pdpMedia.addEventListener('scroll', function () {
        if (dotTick) return;
        dotTick = true;
        requestAnimationFrame(function () { dotTick = false; syncGallery(); });
      }, { passive: true });
      syncGallery();
    }

    /* A horizontal drag on the carousel can still fire a click; only treat it
       as a tap (and open the lightbox) if the pointer barely moved. */
    $$('.pdp-shot img', pdpMedia).forEach(function (im) {
      /* Zoom was pointer-only: no tab stop, no key handler, so a keyboard-only
         buyer could never inspect a $400 piece close up. Every other custom
         control in this file already pairs tabindex with a keydown handler. */
      im.setAttribute('role', 'button');
      im.setAttribute('tabindex', '0');
      im.setAttribute('aria-label', 'Enlarge image' + (im.alt ? ': ' + im.alt : ''));
      var dx = 0, dy = 0;
      im.addEventListener('pointerdown', function (e) { dx = e.clientX; dy = e.clientY; });
      im.addEventListener('click', function (e) {
        if (Math.abs(e.clientX - dx) > 10 || Math.abs(e.clientY - dy) > 10) return;
        openLightbox(im);
      });
      im.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLightbox(im); }
      });
    });
  }

  /* Remembers which thumbnail opened the viewer so focus can be handed back
     exactly where it came from, rather than dumped at the top of the document. */
  var lightboxOpener = null;

  function openLightbox(im) {
    if (!lightbox || !lightboxImg) return;
    lightboxOpener = im;
    lightboxImg.src = im.getAttribute('data-full') || im.src;
    lightboxImg.alt = im.alt || '';
    lightbox.classList.add('on');
    lightbox.setAttribute('aria-hidden', 'false');
    setInert(true);
    var c = $('#lightbox-close');
    if (c) c.focus({ preventScroll: true });
  }

  function closeLightbox() {
    if (!lightbox || !lightbox.classList.contains('on')) return;
    lightbox.classList.remove('on');
    lightbox.setAttribute('aria-hidden', 'true');
    setInert(false);
    if (lightboxOpener) { lightboxOpener.focus({ preventScroll: true }); lightboxOpener = null; }
  }

  if (lightbox) {
    lightbox.addEventListener('click', function (e) {
      /* Backdrop and the explicit close button dismiss it; clicking the photo
         itself must not, or the zoom shuts the instant you try to look at it. */
      if (e.target === lightbox || e.target.closest('#lightbox-close')) closeLightbox();
    });
  }

  /* ------------------------------------------------------- SIZE & FIT PANEL
     Not a static help sheet any more: every row is a real, selectable variant,
     so a buyer can read what each length actually does on the body, choose it,
     and add to cart without leaving the panel. #sguide-open is a real <button>
     now, so Enter/Space are handled natively -- no keydown shim needed. */
  var sgmodal = $('#sgmodal');

  /* The panel is rendered by the product section, so it sits inside
     #MainContent -- which is one of the regions inerted whenever a modal opens.
     Left there, opening the size guide would make the size guide itself inert:
     visible, focusable-looking, and completely unclickable. Reparenting it to
     <body> puts it outside everything setInert() touches. The lightbox and cart
     drawer already live at body level via the chrome section; this brings the
     guide into line with them. */
  /* Reparenting is idempotent per page load, but the Theme Editor re-renders the
     product section in place -- which produces a SECOND #sgmodal and a second
     .pdp-sticky inside #MainContent while the originals sit on <body>. Duplicate
     ids break every querySelector after them. Drop any body-level copy first so
     the freshly rendered one is always the only one. Editor-only, but a
     duplicate id is the kind of thing that silently poisons a later fix. */
  function reparentToBody(sel) {
    var live = $(sel);
    if (!live) return null;
    $$(sel).forEach(function (old) {
      if (old !== live && old.parentElement === document.body) old.remove();
    });
    if (live.parentElement !== document.body) document.body.appendChild(live);
    return live;
  }

  if (sgmodal) sgmodal = reparentToBody('#sgmodal');

  /* THE STICKY BAR WAS UNCLICKABLE. Same reason, one layer down.

     .pdp-sticky is rendered by the product section, so it sits inside
     #MainContent -- which carries `position: relative; z-index: 2` and is
     therefore a stacking context. The footer is its own section group, ALSO at
     z-index 2, and it comes later in the DOM. Equal z-index, later sibling wins:
     the footer painted over the fixed bar wherever the two overlapped, which is
     the whole bottom of the viewport once you scroll far enough.

     The bar's own z-index 300 cannot help -- it only ranks inside #MainContent's
     context, it cannot outrank #MainContent's sibling. So the button was
     rendered, visible, enabled, not disabled, hit-testable in isolation -- and
     every real click landed on <footer id="cs-foot"> instead. Nothing in the
     console, nothing in the network log, no error anywhere. Confirmed by
     elementFromPoint at the button's own centre returning FOOTER#cs-foot.

     Reparenting to <body> takes it out of that context so its z-index sorts
     globally, exactly as the cart drawer, the lightbox and the size panel
     already do. Listeners survive a move, so this is safe wherever it runs. */
  /* ...but appending to <body> put the bar AFTER the footer in the DOM, so the
     mobile Add to cart -- the primary purchase control on a phone -- became the
     very LAST thing in the tab order. A keyboard or switch user had to pass
     every footer and policy link to reach it. Insert it before the footer's
     section group instead. Painting is unaffected: it is still a <body> child,
     so its z-index sorts globally, and z-index outranks source order. */
  (function placeSticky() {
    var bar = reparentToBody('.pdp-sticky');
    if (!bar) return;
    var foot = $('#cs-foot');
    while (foot && foot.parentElement !== document.body) foot = foot.parentElement;
    if (foot && foot !== bar) document.body.insertBefore(bar, foot);
  })();

  var sgOpen = $('#sguide-open');
  var sgClose = $('#sg-close');
  var sgAdd = $('#sg-add');
  var sgHint = $('#sg-hint');
  var sgRows = $$('.sg-row');

  function setSgHint(t) {
    if (!sgHint) return;
    sgHint.textContent = t || '';
    sgHint.classList.toggle('show', !!t);
  }

  function openSizeGuide() {
    if (!sgmodal) return;
    sgmodal.classList.add('on');
    sgmodal.setAttribute('aria-hidden', 'false');
    /* Hides the pinned bar while the panel is up -- see N5 in the CSS. */
    body.classList.add('sg-open');
    setInert(true);
    /* The panel is visibility:hidden until .on lands, and an element cannot take
       focus while it is still hidden. Waiting a frame lets the style apply
       first, otherwise focus silently falls back to <body>. */
    requestAnimationFrame(function () {
      if (sgClose) sgClose.focus({ preventScroll: true });
    });
  }

  function closeSizeGuide() {
    if (!sgmodal || !sgmodal.classList.contains('on')) return;
    sgmodal.classList.remove('on');
    sgmodal.setAttribute('aria-hidden', 'true');
    body.classList.remove('sg-open');
    setSgHint('');
    setInert(false);
    if (sgOpen) sgOpen.focus({ preventScroll: true });
  }

  if (sgOpen) sgOpen.addEventListener('click', openSizeGuide);
  if (sgClose) sgClose.addEventListener('click', closeSizeGuide);
  if (sgmodal) {
    sgmodal.addEventListener('click', function (e) {
      if (e.target === sgmodal) closeSizeGuide();
    });
  }

  sgRows.forEach(function (r) {
    r.addEventListener('click', function () {
      if (r.disabled) return;
      selectVariant(r.dataset.variantId);
      setSgHint('');
    });
  });

  if (sgAdd) {
    sgAdd.addEventListener('click', function () {
      var v = selectedVariant();
      if (!v) {
        setSgHint('Choose a length.');
        var first = sgmodal && sgmodal.querySelector('.sg-row:not([disabled])');
        if (first) first.focus();
        return;
      }
      closeSizeGuide();
      addToCart(v);
    });
  }

  /* ------------------------------------------------- VARIANT SELECTION */
  var variantInput = $('#pdp-variant-id');
  var priceEl = $('#pdp-price');
  var stickyPrice = $('#sticky-price');
  var sizeWrap = $('.size-pills');
  var hintEl = $('#pdp-size-hint');
  var atc = $('#buy-atc');
  var stickyAtc = $('#sticky-atc');

  /* The single source of truth for "which length is chosen". Both the pills on
     the page and the rows inside the size panel route through this, so the two
     can never drift apart and show the buyer two different selections. */
  function selectVariant(id) {
    if (!id) return;
    var chosen = null;
    $$('.size-pill').forEach(function (p) {
      var on = p.dataset.variantId === id;
      p.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) chosen = p;
    });
    $$('.sg-row').forEach(function (r) {
      r.setAttribute('aria-pressed', r.dataset.variantId === id ? 'true' : 'false');
    });
    if (variantInput) variantInput.value = id;
    if (chosen && chosen.dataset.price) {
      if (priceEl) priceEl.textContent = chosen.dataset.price;
      if (stickyPrice) stickyPrice.textContent = chosen.dataset.price;
      /* The Afterpay elements (crestside-product.liquid): desktop's above
         the ATC button, mobile's under the price -- CSS shows one per
         breakpoint. Their component re-renders when data-amount changes,
         so the instalment line follows the chosen length. data-price is
         "$390.00 AUD"; the component wants the bare number. BOTH update so
         neither can go stale across a viewport rotate. All pieces are
         uniform-price today; this keeps the message honest the day one
         is not. */
      var apAmount = chosen.dataset.price.replace(/[^0-9.]/g, '');
      $$('.pdp-afterpay').forEach(function (el) {
        el.setAttribute('data-amount', apAmount);
      });
    }
    if (sizeWrap) sizeWrap.classList.remove('warn');
    if (hintEl) { hintEl.textContent = ''; hintEl.classList.remove('show'); }
  }

  if (sizeWrap) {
    var pills = $$('.size-pill', sizeWrap);
    /* One real size = nothing to choose, so pre-select it. Multiple sizes start
       unselected so the buyer makes a deliberate choice and we never guess. */
    if (pills.length === 1 && !pills[0].disabled) selectVariant(pills[0].dataset.variantId);
    pills.forEach(function (p) {
      p.addEventListener('click', function () {
        if (p.disabled) return;
        selectVariant(p.dataset.variantId);
      });
    });
  }

  function selectedVariant() {
    if (!sizeWrap) return null;
    var on = sizeWrap.querySelector('.size-pill[aria-pressed="true"]');
    return on ? on.dataset.variantId : null;
  }

  function requireSize() {
    if (sizeWrap) sizeWrap.classList.add('warn');
    if (hintEl) { hintEl.textContent = 'Choose a length.'; hintEl.classList.add('show'); }
    var first = sizeWrap && sizeWrap.querySelector('.size-pill:not([disabled])');
    if (first) first.focus();
  }

  /* --------------------------------------------------------------- CART */
  var drawer = $('#cart-drawer');
  var overlay = $('#cart-overlay');
  var cartBtn = $('#nav-cart');
  var cartItemsEl = $('#cart-items');
  var cartCountEl = $('#cart-count');
  var cartSubEl = $('#cart-sub');
  var cartCheckout = $('#cart-checkout');
  var cartMsg = $('#cart-msg');

  /* #cart-msg is `display:none` until it gets an `.on` class (crestside.css:474-478),
     and nothing ever added that class -- every cart error was written into the
     DOM and then rendered invisible. A buyer whose add or quantity change was
     rejected saw no explanation at all. */
  function setCartMsg(text) {
    if (!cartMsg) return;
    cartMsg.textContent = text || '';
    cartMsg.classList.toggle('on', !!text);
  }

  /* Blanket-inerting every body child would also hit Shopify's own
     #shopify-section-* wrappers, script tags and the Theme Editor's injected
     nodes. Scope it to the real content regions only. */
  /* #cs-foot belongs here. The footer is its own section group, a sibling of
     #MainContent -- so with the cart drawer, lightbox or size panel open, tabbing
     past the last control inside the dialog dropped focus into the footer's
     policy and mailto links instead of staying contained. The gate's own list
     already had it; these three did not. */
  /* `.cs-skip` is a <body>-level focusable that nothing else covers: with the
     cart drawer open, tabbing backwards off the first control escaped the
     dialog and landed on the skip link, which then jumped the page out from
     under the open drawer. */
  var INERT_TARGETS = ['#site', '#MainContent', '#gate', '#cs-nav', '#cs-foot', '.cs-logo-fixed', '.pdp-sticky', '.logo-stage', '.cs-skip'];
  function setInert(on) {
    INERT_TARGETS.forEach(function (sel) {
      $$(sel).forEach(function (el) {
        if (on) el.setAttribute('inert', ''); else el.removeAttribute('inert');
      });
    });
  }
  function openCart() {
    body.classList.add('cart-open');
    if (cartBtn) cartBtn.setAttribute('aria-expanded', 'true');
    setInert(true);
    var c = $('#cart-close');
    if (c) c.focus({ preventScroll: true });
  }
  function closeCart() {
    body.classList.remove('cart-open');
    if (cartBtn) cartBtn.setAttribute('aria-expanded', 'false');
    setCartMsg('');
    setInert(false);
    if (cartBtn) cartBtn.focus({ preventScroll: true });
  }

  function renderCart(cart) {
    /* Shopify's 4xx bodies (e.g. "Only 1 item was added... due to availability")
       are valid JSON, so they parse fine and used to flow straight through here:
       money(undefined) painted a literal "$NaN" subtotal and item_count printed
       "undefined", right next to the checkout button. Anything that is not a
       real cart is refused outright, so a failed request can never repaint the
       drawer with nonsense -- the last good state simply stays put. */
    if (!cart || typeof cart.item_count !== 'number' || !Array.isArray(cart.items)) return;

    if (cartCountEl) cartCountEl.textContent = cart.item_count;
    if (cartBtn) cartBtn.dataset.filled = cart.item_count ? '1' : '0';
    /* Subtotal carries the currency, line prices do not -- matching the product
       page, which prints "AUD" once beside the price and never on a spec row. */
    if (cartSubEl) cartSubEl.textContent = money(cart.total_price, true);
    if (cartCheckout) cartCheckout.disabled = cart.item_count === 0;
    if (!cartItemsEl) return;

    /* FOCUS SURVIVES THE RE-RENDER.

       `innerHTML = ''` below destroys every control in the drawer, including
       the one the buyer just activated. Focus then falls to <body>, so a
       keyboard or screen-reader buyer pressing "+" lost their place on EVERY
       quantity change and had to tab in again from the top of the document.
       Sighted mouse users never saw it, which is why it survived this long.

       Capture what was focused, rebuild, then put focus back on the same
       control. If that control is gone -- the line was removed, or it was the
       last one -- fall through to the nearest sensible thing still on screen
       rather than dropping focus on the floor. */
    var prev = document.activeElement;
    var tok = prev && prev.dataset && prev.dataset.cartAct
      ? { act: prev.dataset.cartAct, key: prev.dataset.cartKey } : null;

    function restore() {
      if (!tok) return;
      var sel = '[data-cart-act="' + tok.act + '"][data-cart-key="' + tok.key + '"]';
      var el = cartItemsEl.querySelector(sel)
            || cartItemsEl.querySelector('[data-cart-key="' + tok.key + '"]')
            || cartItemsEl.querySelector('[data-cart-act="remove"]')
            || cartCheckout
            || $('#cart-close');
      if (el && !el.disabled) el.focus({ preventScroll: true });
      else { var c = $('#cart-close'); if (c) c.focus({ preventScroll: true }); }
    }

    if (!cart.items.length) {
      cartItemsEl.innerHTML = '<p class="cart-empty">Your cart is empty</p>';
      restore();
      return;
    }
    cartItemsEl.innerHTML = '';
    cart.items.forEach(function (line, idx) {
      var row = document.createElement('div');
      row.className = 'cart-line';
      if (line.image) {
        var im = document.createElement('img');
        im.src = line.image.replace(/(\.[a-z]+)(\?|$)/i, '_132x$1$2');
        im.width = 66; im.height = 66; im.alt = ''; im.loading = 'lazy';
        row.appendChild(im);
      }
      var info = document.createElement('div');
      var nm = document.createElement('p');
      nm.className = 'cart-line-name';
      nm.textContent = line.product_title;
      info.appendChild(nm);
      if (line.variant_title && line.variant_title !== 'Default Title') {
        var sz = document.createElement('p');
        sz.className = 'cart-line-size';
        sz.textContent = 'Size: ' + line.variant_title;
        info.appendChild(sz);
      }
      var pr = document.createElement('p');
      pr.className = 'cart-line-price';
      pr.textContent = money(line.final_line_price);
      info.appendChild(pr);

      var ctl = document.createElement('div');
      ctl.className = 'cart-line-row';
      var qty = document.createElement('div');
      qty.className = 'cart-qty';
      /* line.key, not idx + 1. /cart/change.js's `line` parameter is a POSITION
         in the cart as the SERVER currently holds it, and the cart is one shared
         object across every tab. Remove the first item in another tab and every
         later line shifts up one -- so a "+" clicked against this render would
         silently change the quantity of, or delete, a different piece. The line
         key is stable and survives reordering. */
      /* data-cart-act / data-cart-key exist purely so focus can be put back on
         the equivalent control after the re-render below wipes this node. */
      var minus = document.createElement('button');
      minus.type = 'button'; minus.textContent = '−';
      minus.setAttribute('aria-label', 'Decrease quantity');
      minus.dataset.cartAct = 'minus'; minus.dataset.cartKey = line.key;
      minus.addEventListener('click', function () { changeLine(line.key, line.quantity - 1); });
      var num = document.createElement('span');
      num.textContent = line.quantity;
      var plus = document.createElement('button');
      plus.type = 'button'; plus.textContent = '+';
      plus.setAttribute('aria-label', 'Increase quantity');
      plus.dataset.cartAct = 'plus'; plus.dataset.cartKey = line.key;
      plus.addEventListener('click', function () { changeLine(line.key, line.quantity + 1); });
      qty.appendChild(minus); qty.appendChild(num); qty.appendChild(plus);

      var rm = document.createElement('button');
      rm.type = 'button'; rm.className = 'cart-remove'; rm.textContent = 'Remove';
      rm.setAttribute('aria-label', 'Remove ' + line.product_title + ' from cart');
      rm.dataset.cartAct = 'remove'; rm.dataset.cartKey = line.key;
      rm.addEventListener('click', function () { changeLine(line.key, 0); });

      ctl.appendChild(qty); ctl.appendChild(rm);
      info.appendChild(ctl);
      row.appendChild(info);
      cartItemsEl.appendChild(row);
    });
    restore();
  }

  /* `critical` marks the refresh that runs straight after a successful add. That
     one gets a single retry, because its failure is the one a buyer actually
     sees: the add really did land on the server, the drawer said "Added to
     cart", and the drawer still showed the PRE-add contents because its own
     /cart.js follow-up hit a transient 429. Caught live during a regression
     sweep. Everywhere else, keeping the last known-good cart on screen is still
     the right silent behaviour. */
  function refreshCart(critical) {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        /* Same guard changeLine() already has. Without it, anything that returns
           HTML instead of JSON -- a Cloudflare challenge page, a maintenance
           response -- throws a raw SyntaxError into the console and leaves the
           drawer in whatever half-state it was in. Observed live during QA. */
        if (!r.ok) throw new Error('cart unavailable');
        return r.json();
      })
      .then(renderCart)
      .catch(function () {
        if (!critical) return; /* keep the last known-good cart on screen */
        return new Promise(function (res) { setTimeout(res, 700); })
          .then(function () {
            return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
              .then(function (r) { if (!r.ok) throw new Error('cart unavailable'); return r.json(); })
              .then(renderCart)
              .catch(function () { /* second failure: leave the last good state */ });
          });
      });
  }

  /* `key` is Shopify's stable line key (variantId:hash). Passed as `id`, which
     /cart/change.js accepts in place of the positional `line` -- see the note in
     renderCart() for why position addressing was unsafe. */
  /* One cart mutation at a time. The +/- buttons compute the new quantity from
     the quantity captured at the LAST RENDER, and nothing stopped a second click
     landing before the first response re-rendered the row. Two fast taps on "+"
     from a quantity of 1 both read 1 and both posted 2, so the cart finished at
     2 instead of 3 -- one tap silently discarded, both requests successful, no
     error shown, and the drawer never displayed 3 at any point. addToCart has
     had this guard since the double-charge bug; the quantity controls never got
     it. */
  var changing = false;

  function changeLine(key, quantity) {
    if (changing) return Promise.resolve();
    changing = true;
    setCartMsg('');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        /* This check was missing entirely, which is what produced the "$NaN"
           subtotal: raising quantity past available stock returns 422 with a
           JSON body that has no total_price, and it was rendered as-is. */
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (e) {
            throw new Error(e.description || e.message || 'Could not update the cart.');
          });
        }
        /* The .catch belongs on the SUCCESS branch too. A 2xx carrying a
           non-JSON body -- a TLS-inspecting corporate proxy, an extension that
           synthesises a 200 -- makes r.json() reject with a SyntaxError, and the
           final .catch below prints it verbatim: the buyer reads "Unexpected
           token '<' is not valid JSON" next to a $1,290 chain. The failure
           branch was hardened against exactly this and the success branch was
           not. */
        return r.json().catch(function () {
          throw new Error('Could not update the cart. Please try again.');
        });
      })
      .then(renderCart)
      .catch(function (err) {
        setCartMsg(err.message || 'Could not update the cart. Please try again.');
        /* Resync from the server so the drawer shows what the cart ACTUALLY
           holds. Without this the rejected quantity stays on screen and the
           buyer believes a change landed that never did. */
        return refreshCart();
      })
      .then(function () { changing = false; });
  }

  /* One add at a time. Without this, a double click -- or a double tap on a slow
     mobile connection, where nothing appears to have happened yet -- fires two
     independent /cart/add.js posts. Both succeed, and the buyer is charged for
     two of a $1,290 chain unless they read the drawer carefully before checkout.
     Guards every entry point, since the PDP button, the sticky bar and the size
     panel all funnel through here. */
  var adding = false;

  function addToCart(variantId) {
    if (adding) return Promise.resolve();
    adding = true;
    setCartMsg('');
    if (atc) atc.setAttribute('aria-busy', 'true');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (r) {
        /* .catch on the body parse, matching changeLine(). Without it, a 5xx or
           a WAF challenge page returns HTML, r.json() rejects with a raw
           SyntaxError, and "Unexpected token < in JSON" is what the buyer reads. */
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (e) {
            throw new Error(e.description || e.message || 'Could not add to cart.');
          });
        }
        /* Same guard on the success branch -- see the note in changeLine(). */
        return r.json().catch(function () {
          throw new Error('Could not add to cart. Please try again.');
        });
      })
      .then(function () { return refreshCart(true); })
      .then(function () {
        openCart();
        /* #cart-msg is the drawer's role="status" region, and it was only ever
           written on the error path -- so a screen-reader buyer got the drawer
           opening and total silence about whether the add worked, on every
           single purchase. */
        setCartMsg('Added to cart.');
      })
      .catch(function (err) {
        openCart();
        setCartMsg(err.message || 'Could not add to cart.');
        /* The add failed, so whatever the drawer is showing may be stale --
           pull the real cart rather than leaving a wrong count on screen. */
        return refreshCart();
      })
      .then(function () {
        adding = false;
        if (atc) atc.removeAttribute('aria-busy');
      });
  }

  function handleAdd(e, fromSticky) {
    var v = selectedVariant();
    if (!v) {
      if (e) e.preventDefault();
      /* From the sticky bar the size pills are usually thousands of pixels up
         the page, so requireSize()'s inline hint and focus jump happen somewhere
         the buyer cannot see -- the button reads as broken. Open the length
         chooser instead: it is a modal, it is on screen, and it can complete the
         purchase without scrolling back. */
      if (fromSticky && sgmodal) { openSizeGuide(); return; }
      requireSize();
      return;
    }
    if (e) e.preventDefault();
    addToCart(v);
  }

  var pdpForm = $('#pdp-form');
  if (pdpForm) pdpForm.addEventListener('submit', handleAdd);
  if (stickyAtc) stickyAtc.addEventListener('click', function (e) { handleAdd(e, true); });

  /* Founder direction: the buy button should be reachable at any scroll depth.
     On a phone the bar is simply always up (CSS). On desktop the buy column is
     sticky, so the real button is usually already on screen -- and two identical
     Add to cart buttons at once reads as a bug. The bar slides up only once the
     real one has actually left the viewport, which on a long page is exactly
     when the sticky column releases and scrolls away. */
  if (atc && stickyAtc && 'IntersectionObserver' in window) {
    /* rootMargin 0, not -10%. A negative top margin shrinks the observed root,
       so the button counted as "gone" while it was still plainly on screen in
       the top tenth of the viewport -- measured live: at scrollY 2130 of a
       3327px page the real Add to cart sat at y 34-88, fully visible, with the
       floating bar already up. Two identical buy buttons at once is exactly what
       the delayed reveal existed to prevent. */
    new IntersectionObserver(function (entries) {
      body.classList.toggle('atc-away', !entries[0].isIntersecting);
    }, { rootMargin: '0px' }).observe(atc);
  }

  /* The buy column is sticky within its grid row. Near the end of a long product
     page the row runs out and the column scrolls up with the page -- passing
     behind the docked logo, which is fixed at z-index 300. The product title and
     the first lines of the description were being chopped in half by it.

     Rather than move either element (both are signed off) the page fades content
     out before it reaches the mark -- but only while the collision is actually
     possible, so the star crown behind the top of the page is untouched the rest
     of the time.

     THRESHOLD 130, NOT 190. 190 was wrong in a way that defeated the whole
     point: R1 pins .pdp-buy at top:160px on desktop because that is its natural
     resting offset, so `160 < 190` was true before the visitor had touched the
     wheel. The class latched on at page load and never came off -- a permanent
     scrim over the top 150px of every product page, sitting on the star crown
     (216 stars seeded at 1-14vh). The one thing the Continuum rule forbids is a
     visible break between the gate's sky and the store's, and that was one.

     130 is just under the docked logo's bottom edge (~126px desktop, ~91px
     mobile). While the column is pinned at 160 the fade is off; it comes up only
     once the column has actually released and risen far enough to collide. */
  var buyCol = $('.pdp-buy');
  if (buyCol && document.body.classList.contains('pdp-page')) {
    var fadeTick = false;
    var syncFade = function () {
      var high = buyCol.getBoundingClientRect().top < 130;
      body.classList.toggle('buy-high', high);
    };
    window.addEventListener('scroll', function () {
      if (fadeTick) return;
      fadeTick = true;
      requestAnimationFrame(function () { fadeTick = false; syncFade(); });
    }, { passive: true });
    window.addEventListener('resize', syncFade);
    syncFade();
  }

  /* Same fade, same reasoning, for the long-form templates -- which carried it
     PERMANENTLY because there was no buy-high equivalent to key off. That put
     a black band across the star crown on About, Why 925 and every policy page
     at rest, reintroducing the Continuum break the PDP version exists to
     prevent. The prose still needs protecting once it moves under the docked
     logo, so the fade arrives on first scroll rather than at page load.

     24px, not 0: enough to survive scroll-anchoring jitter and a browser
     restoring a near-top scroll position, without letting a real scroll pass
     unfaded. */
  /* Shopify serves policy pages with body class `template-null`, NOT
     `template-policy` -- measured live, the class list is
     "gradient cs-body template-null entered no-gate". Every rule scoped to
     `body.template-policy` in crestside-shopify.css was therefore DEAD, which
     is why the policy pages had no top fade at all (the element measured
     height 0, position static, background none) while About and Why 925 had
     one permanently. Note it still rendered, because the empty-element guard
     forces display:block on it -- it simply had no geometry.

     So the fade is tagged from the DOM rather than from a template name:
     the Shopify-owned policy wrapper is a fact, `template-policy` was a
     guess. */
  var isLongForm = body.classList.contains('template-page')
                || !!$('.shopify-policy__container');
  if (isLongForm) {
    body.classList.add('long-form');
    var scrollTick = false;
    var syncScrolled = function () {
      body.classList.toggle('scrolled', (window.pageYOffset || document.documentElement.scrollTop) > 24);
    };
    window.addEventListener('scroll', function () {
      if (scrollTick) return;
      scrollTick = true;
      requestAnimationFrame(function () { scrollTick = false; syncScrolled(); });
    }, { passive: true });
    syncScrolled();
  }

  if (cartBtn) cartBtn.addEventListener('click', function () { openCart(); });
  if ($('#cart-close')) $('#cart-close').addEventListener('click', closeCart);
  if (overlay) overlay.addEventListener('click', closeCart);

  /* Wire the server-rendered rows so the drawer works before any JS re-render. */
  if (cartItemsEl) {
    cartItemsEl.addEventListener('click', function (e) {
      var b = e.target.closest('[data-qty]');
      if (!b) return;
      e.preventDefault();
      changeLine(b.dataset.key, Number(b.dataset.qty));
    });
  }

  /* ------------------------------------------------- /cart PAGE CONTROLS
     The cart page posts natively to /cart with `updates[<line key>]`, which is
     correct and works with no JS -- but it is a page-load-time snapshot of a
     cart that anything else can change underneath it. Mutate the cart from the
     drawer (or another tab) and the page's keys go stale; Shopify then answers
     the next click with a 422 and replaces the whole store with its own unstyled
     "Something went wrong / updates parameter is invalid" page. A buyer with a
     $1,290 chain in the cart is dropped out of the brand entirely.

     So JS takes the click when it is available: same line keys, but through
     /cart/change.js, where a refusal can be caught and answered in place. On
     success the page reloads so the server -- not this snapshot -- decides what
     the cart says. The name="updates[...]" attributes stay on the buttons, so
     with scripting off the native post still works exactly as before. */
  var cartPage = $('#cart-page-form');
  if (cartPage) {
    var cartPageMsg = $('#cart-page-msg');
    cartPage.addEventListener('click', function (e) {
      var b = e.target.closest('[data-cartkey]');
      if (!b) return;
      e.preventDefault();
      if (b.disabled) return;
      b.disabled = true;
      if (cartPageMsg) { cartPageMsg.textContent = ''; cartPageMsg.classList.remove('on'); }
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: b.dataset.cartkey, quantity: Number(b.dataset.qty) })
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().catch(function () { return {}; }).then(function (er) {
              throw new Error(er.description || er.message || 'Could not update the cart.');
            });
          }
          window.location.reload();
        })
        .catch(function (err) {
          b.disabled = false;
          if (cartPageMsg) {
            /* Shopify answers a stale line key with "no valid id or line
               parameter" -- accurate, and meaningless to a buyer. This branch
               only ever fires because the cart moved underneath the page, so say
               that instead of quoting the API. Any other message is passed
               through, since those (stock limits) are worth reading verbatim. */
            cartPageMsg.textContent = /valid id or line/i.test(err.message)
              ? 'Your cart was updated in another tab. Refreshing.'
              : err.message;
            cartPageMsg.classList.add('on');
          }
          /* The page is now provably out of step with the server, so show what
             the cart actually holds rather than leaving a wrong row on screen. */
          setTimeout(function () { window.location.reload(); }, 2600);
        });
    });
  }

  /* ------------------------------------------------------------ ESCAPE */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (lightbox && lightbox.classList.contains('on')) { closeLightbox(); return; }
    if (sgmodal && sgmodal.classList.contains('on')) { closeSizeGuide(); return; }
    if (body.classList.contains('cart-open')) closeCart();
  });

  /* The Theme Editor swaps section DOM in and out. Re-running the whole IIFE
     would double-seed the star field and re-bind every listener, so just reload
     the section's dynamic bits. */
  document.addEventListener('shopify:section:load', function () {
    if (typeof refreshCart === 'function') refreshCart();
  });
})();
